import { SyntheticEvent, useCallback, useMemo, useRef, useState } from "react";
import { NavigateAction, SlotInfo, View } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";

import { CalendarHelper } from "@/common/helpers/calendar";
import { TypeHelper } from "@/common/helpers/type";
import { useLatestRef } from "@/common/hooks/ref/useLatestRef";
import { DateRange } from "@/common/ts/datetime";

import { CalendarProps, CalendarView, GenericCalendarEntry } from "../components/Calendar/Calendar";

export interface UseCalendarProps<TEntry extends GenericCalendarEntry> {
  calendarProps: CalendarProps<TEntry>;
}

export interface UseCalendarResult<TEntry extends GenericCalendarEntry> {
  entries: TEntry[];
  nowDate: Date;
  currentDate: Date;
  currentView: CalendarView;
  entryViewPopoverAnchorEl: HTMLElement | null;
  entryToView: TEntry | null;
  handleYearViewDrilldown: (date: Date) => void;
  handleViewChange: (newView: View) => void;
  handleDateChange: (newDate: Date, view: View, action: NavigateAction) => void;
  handleViewDateRangeChange: (
    range: Date[] | { start: Date; end: Date },
    view?: CalendarView,
  ) => void;
  handleSelectEvent: (entry: TEntry, e: SyntheticEvent<HTMLElement>) => void;
  handleSelectSlot: (slotInfo: SlotInfo) => void;
  openEntryViewPopover: (params: { entry: TEntry; anchorEl: HTMLElement }) => void;
  closeEntryViewPopover: (params: { entry: TEntry | null }) => void;
}

export function useCalendar<TEntry extends GenericCalendarEntry>({
  calendarProps,
}: UseCalendarProps<TEntry>): UseCalendarResult<TEntry> {
  const {
    entries,
    defaultView,
    defaultDate,
    isReadOnly,
    renderEntryTitle,
    onViewChange,
    onDateChange,
    onViewDateRangeChange,
    onDateRangeChange,
    onEntryClick,
    onSlotClick,
  } = calendarProps;

  const [nowDate] = useState<Date>(new Date());
  const [currentDate, setCurrentDate] = useState<Date>(defaultDate || new Date());
  const [currentView, setCurrentView] = useState<CalendarView>(defaultView || CalendarView.Month);

  // ensure entry to view is latest value when entries change
  const [entryIdToView, setEntryIdToView] = useState<string | null>(null);
  const [entryViewPopoverAnchorEl, setEntryViewPopoverAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const entryToView = useMemo(
    () => entries.find((x) => x.id === entryIdToView) || null,
    [entries, entryIdToView],
  );

  const onViewChangeRef = useLatestRef(onViewChange);
  const onDateChangeRef = useLatestRef(onDateChange);
  const onViewDateRangeChangeRef = useLatestRef(onViewDateRangeChange);
  const onDateRangeChangeRef = useLatestRef(onDateRangeChange);
  const onEntryClickRef = useLatestRef(onEntryClick);
  const onSlotClickRef = useLatestRef(onSlotClick);

  const entriesComputed = useMemo(
    () =>
      entries.map((x) => {
        const oldTitle = x.title;
        const newTitle = renderEntryTitle?.(x) || oldTitle;
        return {
          ...x,
          title: newTitle,
          titleStr: TypeHelper.isString(newTitle)
            ? newTitle
            : TypeHelper.isString(oldTitle)
              ? oldTitle
              : undefined,
        };
      }),
    [entries],
  );

  // Fired when date or date range changes by any means.
  const handleDateRangeChange = useCallback(
    (range: Date | Date[] | { start: Date; end: Date }, view: CalendarView) => {
      const dateRangeInclusive = CalendarHelper.getViewDateRangeInclusive(view, range);
      const dateRangeExclusive = CalendarHelper.getViewDateRangeExclusiveFromInclusive(
        view,
        dateRangeInclusive,
      );
      console.log("Calendar.handleDateRangeChange", {
        range,
        view,
        dateRangeInclusive,
        dateRangeExclusive,
      });
      onDateRangeChangeRef.current?.({ dateRangeInclusive, dateRangeExclusive, view });
    },
    [],
  );

  // Switch to day view and set date when a day is clicked in year view
  const handleYearViewDrilldown = useCallback((date: Date) => {
    const view = CalendarView.Day;
    setCurrentView(view);
    setCurrentDate(date);
    onViewChangeRef.current?.(view);
    onDateChangeRef.current?.({ date: date, view: view });
    handleDateRangeChange(date, view);
  }, []);

  const handleViewChange = useCallback((newView: View) => {
    console.log("Calendar.handleViewChange", { newView });
    setCurrentView(newView);
    onViewChangeRef.current?.(newView);
  }, []);

  // Fired when date navigation occurs (back, next, today, drill down to date).
  const handleDateChange = useCallback((newDate: Date, view: View, action: NavigateAction) => {
    console.log("Calendar.handleDateChange (onNavigate)", { newDate, view, action });
    setCurrentDate(newDate);
    onDateChangeRef.current?.({ date: newDate, view });
    handleDateRangeChange(newDate, view);
  }, []);

  // Fired only when view changes.
  const handleViewDateRangeChange = useCallback(
    (range: Date[] | { start: Date; end: Date }, view?: CalendarView) => {
      if (!view) {
        return;
      }
      const dateRangeInclusive = CalendarHelper.getViewDateRangeInclusive(view, range);
      const dateRangeExclusive = CalendarHelper.getViewDateRangeExclusiveFromInclusive(
        view,
        dateRangeInclusive,
      );
      console.log("Calendar.handleViewDateRangeChange (onDateRangeChange)", {
        range,
        view,
        dateRangeInclusive,
        dateRangeExclusive,
      });
      onViewDateRangeChangeRef.current?.({ dateRangeInclusive, dateRangeExclusive, view });
      handleDateRangeChange(range, view);
    },
    [],
  );

  // Callback fired when a calendar event is selected (i.e.: an onClick handler).
  const handleSelectEvent = (entry: TEntry, e: SyntheticEvent<HTMLElement>) => {
    console.log("Calendar.handleSelectEvent", { entry, e });
    setEntryIdToView(entry.id);
    setEntryViewPopoverAnchorEl(e.target as HTMLElement);
    onEntryClickRef.current?.(entry);
  };

  // A callback fired when a date selection is made.
  const handleSelectSlot = (slotInfo: SlotInfo) => {
    const range: DateRange = { start: slotInfo.start, end: slotInfo.end };
    console.log("Calendar.handleSelectSlot", { slotInfo, range });
    if (!isReadOnly) {
      onSlotClickRef.current?.({ range: range, view: currentView });
    }
  };

  const openEntryViewPopover = useCallback(
    (params: { entry: TEntry; anchorEl: HTMLElement }) => {
      setEntryIdToView(params.entry.id);
      setEntryViewPopoverAnchorEl(params.anchorEl);
    },
    [setEntryIdToView, setEntryViewPopoverAnchorEl],
  );

  const closeEntryViewPopover = useCallback(
    (params: { entry: TEntry | null }) => {
      if (!params.entry || params.entry.id === entryIdToView) {
        setEntryIdToView(null);
        setEntryViewPopoverAnchorEl(null);
      }
    },
    [entryIdToView, setEntryIdToView, setEntryViewPopoverAnchorEl],
  );

  // always return the same instance of the result object (same reference),
  // to allow callers to capture the result once and access the same instance regardless of re-renders.
  const result = useRef({} as UseCalendarResult<TEntry>);
  Object.assign(result.current, {
    entries: entriesComputed,
    nowDate,
    currentDate,
    currentView,
    entryViewPopoverAnchorEl,
    entryToView,
    handleYearViewDrilldown,
    handleViewChange,
    handleDateChange,
    handleViewDateRangeChange,
    handleSelectEvent,
    handleSelectSlot,
    openEntryViewPopover,
    closeEntryViewPopover,
  } satisfies UseCalendarResult<TEntry>);
  return result.current;
}
